/**
 * 指挥台 3D 位姿微调：AutoCAD 风格三轴箭头（东/北/上）
 * 叠在独立 WebGL 画布上；相机是「投影=Mapbox VP」的哑相机，拾取用 VP 逆矩阵射射线。
 */
import * as THREE from 'three';

const AXIS = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1)
};

const AXIS_COLOR = {
  x: 0xff3355,
  y: 0x22f0a8,
  z: 0x3d9bff
};

const AXIS_LABEL = { x: '东', y: '北', z: '上' };
const SELECT_COLOR = 0xffe566;
const BASE_LEN = 10;
const CLICK_NUDGE_M = 1;
const CLICK_SLOP_PX = 6;
const HIT_PX = 20;

function disposeObject3d(obj) {
  obj?.traverse?.((node) => {
    if (node.geometry) node.geometry.dispose?.();
    if (node.material) {
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      mats.forEach((m) => {
        m.map?.dispose?.();
        m.dispose?.();
      });
    }
  });
}

function makeMat(color, opacity = 1) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: opacity < 0.99,
    opacity,
    depthTest: false,
    depthWrite: false,
    toneMapped: false
  });
}

function alignY(obj, dir) {
  obj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
}

/** 一支轴：发光箭杆 + 锥头 + 更粗的隐形拾取体 */
function makeAxisArrow(name, color) {
  const dir = AXIS[name];
  const group = new THREE.Group();
  group.userData.axis = name;

  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 6.2, 14), makeMat(color));
  shaft.position.y = 3.5;
  const glow = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.22, 6.2, 14), makeMat(color, 0.22));
  glow.position.y = 3.5;
  const head = new THREE.Mesh(new THREE.ConeGeometry(0.42, 1.7, 18), makeMat(color));
  head.position.y = 7.45;
  const headGlow = new THREE.Mesh(new THREE.ConeGeometry(0.62, 1.9, 18), makeMat(color, 0.2));
  headGlow.position.y = 7.5;
  // 负向短锥：点一下往反方向挪
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.28, 1.1, 14), makeMat(color, 0.85));
  tail.position.y = -0.85;
  tail.rotation.x = Math.PI;

  const pick = new THREE.Mesh(
    new THREE.CylinderGeometry(0.7, 0.7, 9.4, 10),
    new THREE.MeshBasicMaterial({ visible: false, depthTest: false })
  );
  pick.position.y = 3.6;
  pick.userData.axis = name;
  pick.userData.sign = 1;

  const pickNeg = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.55, 2.2, 10),
    new THREE.MeshBasicMaterial({ visible: false, depthTest: false })
  );
  pickNeg.position.y = -0.9;
  pickNeg.userData.axis = name;
  pickNeg.userData.sign = -1;

  const visual = new THREE.Group();
  visual.add(shaft, glow, head, headGlow, tail);
  alignY(visual, dir);
  alignY(pick, dir);
  alignY(pickNeg, dir);
  group.add(visual, pick, pickNeg);
  group.userData.visual = visual;
  group.userData.pickables = [pick, pickNeg];
  group.userData.solids = [shaft, head, tail];
  group.userData.glows = [glow, headGlow];
  group.userData.baseColor = color;
  return group;
}

function makeCenterKnob() {
  const group = new THREE.Group();
  group.userData.axis = 'xy';
  const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.55, 1), makeMat(0xb57bff));
  const halo = new THREE.Mesh(new THREE.IcosahedronGeometry(0.92, 1), makeMat(0x66f0ff, 0.28));
  const pick = new THREE.Mesh(
    new THREE.SphereGeometry(1.15, 16, 12),
    new THREE.MeshBasicMaterial({ visible: false, depthTest: false })
  );
  pick.userData.axis = 'xy';
  pick.userData.sign = 1;
  group.add(core, halo, pick);
  group.userData.core = core;
  group.userData.halo = halo;
  group.userData.pickables = [pick];
  return group;
}

function makeRings() {
  const group = new THREE.Group();
  [4.4, 5.6].forEach((r, i) => {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(r, i === 0 ? 0.045 : 0.028, 8, 72),
      makeMat(0x66f0ff, i === 0 ? 0.38 : 0.2)
    );
    // 默认圆环在 XY（东-北）平面，即水平圈
    group.add(ring);
  });
  return group;
}

function createHud(host) {
  const hud = document.createElement('div');
  hud.className = 'zhxt-ortho-gizmo-hud';
  hud.style.cssText = [
    'position:absolute',
    'inset:0',
    'pointer-events:none',
    'z-index:9',
    'font-family:ui-monospace,Consolas,monospace',
    'letter-spacing:0.14em',
    'font-size:11px',
    'font-weight:700'
  ].join(';');
  const labels = {};
  ['x', 'y', 'z'].forEach((axis) => {
    const el = document.createElement('div');
    el.textContent = AXIS_LABEL[axis];
    const hex = `#${AXIS_COLOR[axis].toString(16).padStart(6, '0')}`;
    el.style.cssText = [
      'position:absolute',
      'transform:translate(-50%,-50%)',
      `color:${hex}`,
      `text-shadow:0 0 8px ${hex},0 0 18px ${hex}`,
      'padding:1px 6px',
      'border-radius:8px',
      'user-select:none',
      'transition:transform 0.12s ease,background 0.12s ease,color 0.12s ease'
    ].join(';');
    hud.appendChild(el);
    labels[axis] = el;
  });
  host.appendChild(hud);
  return { hud, labels };
}

function gizmoWorldMeters(map) {
  try {
    const canvas = map.getCanvas();
    const c = map.project(map.getCenter());
    const a = map.unproject([c.x, c.y]);
    const b = map.unproject([c.x + 96, c.y]);
    const d = a.distanceTo(b);
    if (Number.isFinite(d) && d > 2) return Math.min(64, Math.max(8, d));
  } catch (_) { /* ignore */ }
  return 16;
}

function projectLocal(viewProj, local, canvas) {
  const v = local.clone().applyMatrix4(viewProj);
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  return {
    x: (v.x * 0.5 + 0.5) * w,
    y: (-v.y * 0.5 + 0.5) * h
  };
}

function ndcFromMapPoint(map, point) {
  const canvas = map.getCanvas();
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  return {
    x: (point.x / w) * 2 - 1,
    y: -(point.y / h) * 2 + 1
  };
}

function rayFromNdc(ndc, inv) {
  const near = new THREE.Vector3(ndc.x, ndc.y, -1).applyMatrix4(inv);
  const far = new THREE.Vector3(ndc.x, ndc.y, 1).applyMatrix4(inv);
  const dir = far.sub(near);
  if (dir.lengthSq() < 1e-16) return null;
  return new THREE.Ray(near, dir.normalize());
}

function intersectGround(ndc, inv) {
  const ray = rayFromNdc(ndc, inv);
  if (!ray || Math.abs(ray.direction.z) < 1e-8) return null;
  const t = -ray.origin.z / ray.direction.z;
  if (!Number.isFinite(t)) return null;
  return ray.origin.clone().add(ray.direction.clone().multiplyScalar(t));
}

function offsetLngLatAlt(lng, lat, alt, eastM, northM, upM) {
  const latRad = (lat * Math.PI) / 180;
  const mPerDegLat = 111320;
  const mPerDegLng = 111320 * Math.max(Math.cos(latRad), 0.15);
  return {
    lng: lng + eastM / mPerDegLng,
    lat: lat + northM / mPerDegLat,
    altitude: alt + upM
  };
}

function pixelsPerMeter(viewProj, axis, canvas) {
  const o = projectLocal(viewProj, new THREE.Vector3(0, 0, 0), canvas);
  const t = projectLocal(viewProj, axis.clone().normalize(), canvas);
  const x = t.x - o.x;
  const y = t.y - o.y;
  return { x, y, len: Math.hypot(x, y) };
}

function metersAlongAxis(viewProj, axisName, dx, dy, canvas) {
  const ppm = pixelsPerMeter(viewProj, AXIS[axisName], canvas);
  if (ppm.len < 0.4) return 0;
  return (dx * ppm.x + dy * ppm.y) / (ppm.len * ppm.len);
}

function distPointToSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-6) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function eventPoint(canvasEl, ev) {
  const rect = canvasEl.getBoundingClientRect();
  return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
}

/** 屏幕距离点轴，不依赖哑相机射线（Mapbox VP 下 raycast 经常落空） */
function pickAxis(handle, point) {
  const g = handle?.gizmo;
  if (!g?.viewProj || !handle?.map) return null;
  const canvas = handle.map.getCanvas();
  const worldM = (g.group?.scale?.x || 1) * BASE_LEN;
  const origin = projectLocal(g.viewProj, new THREE.Vector3(0, 0, 0), canvas);
  if (Math.hypot(point.x - origin.x, point.y - origin.y) <= 16) {
    return { axis: 'xy', sign: 1 };
  }
  let best = null;
  let bestD = HIT_PX;
  ['x', 'y', 'z'].forEach((name) => {
    const tip = projectLocal(g.viewProj, AXIS[name].clone().multiplyScalar(worldM * 0.86), canvas);
    const tail = projectLocal(g.viewProj, AXIS[name].clone().multiplyScalar(-worldM * 0.2), canvas);
    const dPos = distPointToSeg(point.x, point.y, origin.x, origin.y, tip.x, tip.y);
    const dNeg = distPointToSeg(point.x, point.y, origin.x, origin.y, tail.x, tail.y);
    if (dPos < bestD) {
      bestD = dPos;
      best = { axis: name, sign: 1 };
    }
    if (dNeg < bestD) {
      bestD = dNeg;
      best = { axis: name, sign: -1 };
    }
  });
  return best;
}

function setMarkerHidden(handle, hidden) {
  try {
    const el = handle.marker?.getElement?.();
    if (el) el.style.display = hidden ? 'none' : '';
  } catch (_) { /* ignore */ }
}

function restoreMapInteraction(handle, gizmo) {
  const map = handle?.map;
  if (!map) return;
  try {
    if (gizmo?._panWasEnabled !== false) map.dragPan.enable();
  } catch (_) { /* ignore */ }
  try {
    map.getCanvas().style.cursor = gizmo?._prevCursor || '';
  } catch (_) { /* ignore */ }
}

function applyMove(handle, next, onMove) {
  handle.pose.lng = next.lng;
  handle.pose.lat = next.lat;
  const payload = { lng: next.lng, lat: next.lat };
  const prevAlt = Number(handle.pose.altitude);
  const nextAlt = Number(next.altitude);
  const altChanged = Number.isFinite(nextAlt) && (!Number.isFinite(prevAlt) || Math.abs(nextAlt - prevAlt) > 0.4);
  if (handle.followTerrain && !altChanged) {
    /* 贴山：只改平面，高度继续跟地形 */
  } else if (Number.isFinite(nextAlt)) {
    handle.pose.altitude = nextAlt;
    handle.followTerrain = false;
    payload.altitude = nextAlt;
  }
  try {
    handle.marker?.setLngLat?.([next.lng, next.lat]);
  } catch (_) { /* ignore */ }
  try {
    onMove?.(payload);
  } catch (_) { /* ignore */ }
  try {
    handle.map.triggerRepaint();
  } catch (_) { /* ignore */ }
}

function setMeshesColor(meshes, hex, opacity) {
  (meshes || []).forEach((mesh) => {
    if (!mesh?.material) return;
    mesh.material.color.setHex(hex);
    if (opacity != null) mesh.material.opacity = opacity;
  });
}

/** 选中轴金色高亮，悬停发白；选中在松开后保持 */
function highlightAxis(gizmo) {
  const selected = gizmo.selected;
  const hover = gizmo.hover;
  ['x', 'y', 'z'].forEach((name) => {
    const grp = gizmo.axes[name];
    if (!grp) return;
    const isSel = selected === name || (selected === 'xy' && name !== 'z');
    const isHov = hover === name && !isSel;
    const base = grp.userData.baseColor;
    const color = isSel ? SELECT_COLOR : (isHov ? 0xffffff : base);
    setMeshesColor(grp.userData.solids, color);
    setMeshesColor(grp.userData.glows, color, isSel ? 0.55 : (isHov ? 0.38 : 0.22));
    grp.userData.visual?.scale?.setScalar(isSel ? 1.28 : (isHov ? 1.12 : 1));
    const el = gizmo.labels?.[name];
    if (el) {
      const hex = `#${(isSel ? SELECT_COLOR : base).toString(16).padStart(6, '0')}`;
      el.style.color = isSel ? '#1a1400' : hex;
      el.style.background = isSel ? 'rgba(255,229,102,0.92)' : (isHov ? 'rgba(255,255,255,0.18)' : 'transparent');
      el.style.textShadow = isSel ? 'none' : `0 0 8px ${hex},0 0 18px ${hex}`;
      el.style.transform = `translate(-50%,-50%) scale(${isSel ? 1.2 : 1})`;
    }
  });
  if (gizmo.center) {
    const xyOn = selected === 'xy' || hover === 'xy';
    gizmo.center.scale.setScalar(selected === 'xy' ? 1.28 : (hover === 'xy' ? 1.14 : 1));
    if (gizmo.center.userData.core?.material) {
      gizmo.center.userData.core.material.color.setHex(selected === 'xy' ? SELECT_COLOR : 0xb57bff);
    }
    if (gizmo.center.userData.halo?.material) {
      gizmo.center.userData.halo.material.opacity = xyOn ? 0.5 : 0.28;
    }
  }
}

/**
 * 每帧：世界轴（不受模型旋转影响）二次绘制 + 标签跟随
 */
export function paintPoseGizmo(handle, ctx) {
  const g = handle?.gizmo;
  if (!g?.active || !ctx) return;
  const { map, camera, renderer, mc, ox, oy, oz, meterScale, matrix } = ctx;
  if (!map || !camera || !renderer) return;

  const worldM = gizmoWorldMeters(map);
  g.group.scale.setScalar(worldM / BASE_LEN);

  const t = performance.now() * 0.001;
  highlightAxis(g);
  if (g.selected === 'xy' || g.hover === 'xy') {
    if (g.center?.userData?.halo?.material) {
      g.center.userData.halo.material.opacity = 0.38 + Math.sin(t * 4) * 0.16;
    }
  } else if (g.center?.userData?.halo?.material && !g.selected) {
    g.center.userData.halo.material.opacity = 0.2 + Math.sin(t * 3.1) * 0.1;
  }
  if (g.center?.userData?.core?.material) {
    g.center.userData.core.material.opacity = 0.82 + Math.sin(t * 2.4) * 0.12;
  }

  const m = new THREE.Matrix4().fromArray(matrix);
  const lGizmo = new THREE.Matrix4()
    .makeTranslation(mc.x - ox, mc.y - oy, mc.z - oz)
    .scale(new THREE.Vector3(meterScale, -meterScale, meterScale));
  const origin = new THREE.Matrix4().makeTranslation(ox, oy, oz);
  const vp = m.multiply(origin).multiply(lGizmo);
  camera.projectionMatrix.copy(vp);
  camera.projectionMatrixInverse.copy(vp).invert();
  g.viewProj.copy(vp);
  g.viewProjInv.copy(camera.projectionMatrixInverse);

  renderer.autoClear = false;
  renderer.clearDepth();
  renderer.render(g.scene, camera);
  renderer.autoClear = true;

  const canvas = map.getCanvas();
  const tip = worldM * 0.82;
  const pts = {
    x: projectLocal(g.viewProj, new THREE.Vector3(tip, 0, 0), canvas),
    y: projectLocal(g.viewProj, new THREE.Vector3(0, tip, 0), canvas),
    z: projectLocal(g.viewProj, new THREE.Vector3(0, 0, tip), canvas)
  };
  ['x', 'y', 'z'].forEach((axis) => {
    const el = g.labels?.[axis];
    if (!el) return;
    el.style.left = `${pts[axis].x}px`;
    el.style.top = `${pts[axis].y}px`;
  });
}

export function detachPoseGizmo(handle) {
  const g = handle?.gizmo;
  if (!g) return;
  try {
    handle.map.off('mousedown', g.onMouseDown);
    handle.map.off('mousemove', g.onMouseMove);
    handle.map.off('click', g.onClick);
  } catch (_) { /* ignore */ }
  try {
    const mapCanvas = handle.map.getCanvas?.();
    mapCanvas?.removeEventListener('pointerdown', g.onPointerDown, true);
  } catch (_) { /* ignore */ }
  try {
    window.removeEventListener('pointerup', g.onWindowUp, true);
    window.removeEventListener('pointermove', g.onWindowMove, true);
    window.removeEventListener('mouseup', g.onWindowUp, true);
    window.removeEventListener('mousemove', g.onWindowMove, true);
  } catch (_) { /* ignore */ }
  restoreMapInteraction(handle, g);
  try {
    g.hud?.remove?.();
  } catch (_) { /* ignore */ }
  try {
    disposeObject3d(g.scene);
  } catch (_) { /* ignore */ }
  setMarkerHidden(handle, false);
  handle.gizmo = null;
}

/**
 * 在已贴地的 overlay handle 上挂/卸三轴工具
 */
export function attachPoseGizmo(handle, onMove) {
  if (!handle || handle.disposed) return false;
  detachPoseGizmo(handle);

  const scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 1));
  const group = new THREE.Group();
  const axes = {
    x: makeAxisArrow('x', AXIS_COLOR.x),
    y: makeAxisArrow('y', AXIS_COLOR.y),
    z: makeAxisArrow('z', AXIS_COLOR.z)
  };
  const center = makeCenterKnob();
  const rings = makeRings();
  group.add(rings, axes.x, axes.y, axes.z, center);
  scene.add(group);

  const pickables = [
    ...axes.x.userData.pickables,
    ...axes.y.userData.pickables,
    ...axes.z.userData.pickables,
    ...center.userData.pickables
  ];

  const host = handle.overlayCanvas?.parentElement
    || handle.map.getCanvasContainer?.()
    || handle.map.getContainer?.();
  const { hud, labels } = host ? createHud(host) : { hud: null, labels: {} };

  const gizmo = {
    active: true,
    scene,
    group,
    axes,
    center,
    pickables,
    hud,
    labels,
    viewProj: new THREE.Matrix4(),
    viewProjInv: new THREE.Matrix4(),
    hover: null,
    selected: null,
    drag: null,
    consumeClick: false,
    onMove,
    _prevCursor: '',
    _panWasEnabled: true
  };

  const canvas = () => handle.map.getCanvas();

  const beginDrag = (point, hit, ev) => {
    ev?.preventDefault?.();
    ev?.stopPropagation?.();
    try {
      gizmo._panWasEnabled = handle.map.dragPan?.isEnabled?.() !== false;
      handle.map.dragPan.disable();
    } catch (_) { /* ignore */ }
    try {
      gizmo._prevCursor = canvas().style.cursor || '';
      canvas().style.cursor = 'grabbing';
    } catch (_) { /* ignore */ }
    gizmo.consumeClick = true;
    gizmo.selected = hit.axis;
    gizmo.hover = hit.axis;
    gizmo.drag = {
      axis: hit.axis,
      sign: hit.sign,
      startPoint: { x: point.x, y: point.y },
      startPose: {
        lng: handle.pose.lng,
        lat: handle.pose.lat,
        altitude: handle.pose.altitude
      },
      viewProj: gizmo.viewProj.clone(),
      viewProjInv: gizmo.viewProjInv.clone(),
      startGround: hit.axis === 'xy'
        ? intersectGround(ndcFromMapPoint(handle.map, point), gizmo.viewProjInv)
        : null,
      moved: false
    };
    highlightAxis(gizmo);
    try {
      handle.map.triggerRepaint();
    } catch (_) { /* ignore */ }
  };

  const endDrag = () => {
    if (!gizmo.drag) return;
    gizmo.drag = null;
    restoreMapInteraction(handle, gizmo);
    highlightAxis(gizmo);
    try {
      handle.map.triggerRepaint();
    } catch (_) { /* ignore */ }
  };

  gizmo.onPointerDown = (ev) => {
    if (ev.button != null && ev.button !== 0) return;
    const point = eventPoint(canvas(), ev);
    const hit = pickAxis(handle, point);
    if (!hit) return;
    beginDrag(point, hit, ev);
  };

  gizmo.onMouseDown = (e) => {
    if (gizmo.drag) return;
    if (e.originalEvent?.button != null && e.originalEvent.button !== 0) return;
    const hit = pickAxis(handle, e.point);
    if (!hit) return;
    beginDrag(e.point, hit, e.originalEvent || e);
    e.preventDefault?.();
  };

  gizmo.onMouseMove = (e) => {
    if (gizmo.drag) return;
    const hit = pickAxis(handle, e.point);
    const next = hit?.axis || null;
    if (next === gizmo.hover) return;
    gizmo.hover = next;
    highlightAxis(gizmo);
    try {
      canvas().style.cursor = next ? 'grab' : (gizmo._prevCursor || '');
    } catch (_) { /* ignore */ }
    try {
      handle.map.triggerRepaint();
    } catch (_) { /* ignore */ }
  };

  gizmo.onWindowMove = (ev) => {
    if (!gizmo.drag) return;
    const point = eventPoint(canvas(), ev);
    const dx = point.x - gizmo.drag.startPoint.x;
    const dy = point.y - gizmo.drag.startPoint.y;
    if (Math.hypot(dx, dy) > CLICK_SLOP_PX) gizmo.drag.moved = true;
    const start = gizmo.drag.startPose;
    if (gizmo.drag.axis === 'xy') {
      const now = intersectGround(ndcFromMapPoint(handle.map, point), gizmo.drag.viewProjInv);
      if (!now || !gizmo.drag.startGround) return;
      const eastM = now.x - gizmo.drag.startGround.x;
      const northM = now.y - gizmo.drag.startGround.y;
      applyMove(handle, offsetLngLatAlt(start.lng, start.lat, start.altitude, eastM, northM, 0), onMove);
      return;
    }
    const meters = metersAlongAxis(gizmo.drag.viewProj, gizmo.drag.axis, dx, dy, canvas());
    const eastM = gizmo.drag.axis === 'x' ? meters : 0;
    const northM = gizmo.drag.axis === 'y' ? meters : 0;
    const upM = gizmo.drag.axis === 'z' ? meters : 0;
    applyMove(handle, offsetLngLatAlt(start.lng, start.lat, start.altitude, eastM, northM, upM), onMove);
  };

  gizmo.onWindowUp = () => {
    if (!gizmo.drag) return;
    if (!gizmo.drag.moved && gizmo.drag.axis !== 'xy') {
      const start = gizmo.drag.startPose;
      const s = (gizmo.drag.sign || 1) * CLICK_NUDGE_M;
      const eastM = gizmo.drag.axis === 'x' ? s : 0;
      const northM = gizmo.drag.axis === 'y' ? s : 0;
      const upM = gizmo.drag.axis === 'z' ? s : 0;
      applyMove(handle, offsetLngLatAlt(start.lng, start.lat, start.altitude, eastM, northM, upM), onMove);
    }
    endDrag();
  };

  gizmo.onClick = (e) => {
    if (!gizmo.consumeClick) return;
    e.preventDefault?.();
    gizmo.consumeClick = false;
  };

  handle.gizmo = gizmo;
  handle.map.on('mousedown', gizmo.onMouseDown);
  handle.map.on('mousemove', gizmo.onMouseMove);
  handle.map.on('click', gizmo.onClick);
  try {
    canvas().addEventListener('pointerdown', gizmo.onPointerDown, true);
  } catch (_) { /* ignore */ }
  window.addEventListener('pointermove', gizmo.onWindowMove, true);
  window.addEventListener('pointerup', gizmo.onWindowUp, true);
  setMarkerHidden(handle, true);
  try {
    handle.map.triggerRepaint();
  } catch (_) { /* ignore */ }
  return true;
}
